import React, { useState, useEffect } from "react";
import { Accordion, AccordionItem, Input, Button, Tabs, Tab, Chip, Switch } from "@heroui/react";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { getSettingDescription } from "./settingsDescriptions";

const isPrimitive = (val: any) => typeof val !== "object" || val === null;

export default function ConfigEditor() {
  const axiosPrivate = useAxiosPrivate();

  const [baseConfig, setBaseConfig] = useState<Record<string, any> | null>(null);
  const [clientConfig, setClientConfig] = useState<Record<string, any> | null>(null);
  const [overrides, setOverrides] = useState<Record<string, any>>({});
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [textareaValues, setTextareaValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchCustomUsecases = async () => {
      try {
        const responseUseCases = await axiosPrivate.get(`/useCases/custom`);
        const baseConfigData = responseUseCases.data["basicSettingsOrder"];
        const clientConfigData = responseUseCases.data["customSettingsOrder"];

        setBaseConfig(baseConfigData);
        setClientConfig(clientConfigData);
        setOverrides(clientConfigData || {});

        if (baseConfigData) {
          setSelectedTab(Object.keys(baseConfigData)[0]);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération du UseCases Custom:", error);
      }
    };

    fetchCustomUsecases();
  }, [axiosPrivate]);

  const updateValueAtPath = (path: string[], value: any) => {
    setOverrides((prev) => {
      const newOverrides = { ...prev };
      let current = newOverrides;

      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!current[key]) {
          current[key] = isNaN(Number(path[i + 1])) ? {} : [];
        }
        current = current[key];
      }

      const lastKey = path[path.length - 1];
      current[lastKey] = value;

      return newOverrides;
    });
  };

  const getEffectiveValue = (path: string[], baseVal: any) => {
    const result = path.reduce((acc, key) => {
      if (acc === undefined) return undefined;
      return acc[key];
    }, overrides);

    return result ?? baseVal;
  };

  const isModified = (path: string[], baseVal: any) => {
    const effectiveVal = getEffectiveValue(path, baseVal);
    if (typeof effectiveVal === "object" && typeof baseVal === "object") {
      return JSON.stringify(effectiveVal) !== JSON.stringify(baseVal);
    }
    return effectiveVal !== baseVal;
  };

  const countModifiedFields = (obj: Record<string, any>, path: string[] = []): number => {
    return Object.entries(obj).reduce((count, [key, value]) => {
      const currentPath = [...path, key];
      if (Array.isArray(value)) {
        return isModified(currentPath, value) ? count + 1 : count;
      } else if (typeof value === "object" && value !== null) {
        return count + countModifiedFields(value, currentPath);
      } else {
        return isModified(currentPath, value) ? count + 1 : count;
      }
    }, 0);
  };

  const getTextareaValue = (path: string[], baseVal: any) => {
    const pathKey = path.join(".");
    const effectiveVal = getEffectiveValue(path, baseVal);

    if (textareaValues[pathKey] === undefined) {
      return JSON.stringify(effectiveVal, null, 2);
    }

    return textareaValues[pathKey];
  };

  const updateTextareaValue = (path: string[], value: string) => {
    const pathKey = path.join(".");

    setTextareaValues((prev) => ({ ...prev, [pathKey]: value }));

    try {
      const parsed = JSON.parse(value);
      updateValueAtPath(path, parsed);
    } catch {
      // JSON invalide pendant l'édition, on ignore silencieusement
    }
  };

  const renderFields = (obj: Record<string, any>, path: string[]) =>
    Object.entries(obj).map(([key, baseVal]) => {
      const currentPath = [...path, key];
      const effectiveVal = getEffectiveValue(currentPath, baseVal);

      if (Array.isArray(baseVal)) {
        return (
          <div key={currentPath.join(".")} className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium">
                {key}
                {getSettingDescription([...path, key].join('.')) && (
                  <span className="text-xs text-gray-500 ml-1">({getSettingDescription([...path, key].join('.'))})</span>
                )}
                {" "}
                {isModified(currentPath, baseVal) && (
                  <Chip size="sm" color="primary">
                    Updated
                  </Chip>
                )}
              </div>
            </div>
            <textarea
              className="w-full h-40 p-2 border rounded"
              value={getTextareaValue(currentPath, baseVal)}
              onChange={(e) => updateTextareaValue(currentPath, e.target.value)}
            />
          </div>
        );
      }

      return (
        <div key={currentPath.join(".")} className="flex items-center gap-2 mb-2">
          <div className="w-[450px] text-sm font-medium items-center">
            {key}
            {getSettingDescription([...path, key].join('.')) && (
              <span className="text-xs text-gray-500 ml-1">({getSettingDescription([...path, key].join('.'))})</span>
            )}
            {" "}
            {isModified(currentPath, baseVal) && (
              <Chip size="sm" color="primary">
                Updated
              </Chip>
            )}
          </div>
          {typeof effectiveVal === "boolean" ? (
            <Switch
              isSelected={effectiveVal}
              onValueChange={(value) => {
                updateValueAtPath(currentPath, value);
              }}
            ></Switch>
          ) : (
            <Input
              value={String(effectiveVal ?? "")}
              onChange={(e) => {
                if (e.target.value === "true" || e.target.value === "false") {
                  updateValueAtPath(currentPath, e.target.value === "true");
                } else if (e.target.value !== "" && !Number.isNaN(Number(e.target.value))) {
                  updateValueAtPath(currentPath, Number(e.target.value));
                } else {
                  updateValueAtPath(currentPath, e.target.value);
                }
              }}
              className="flex-1"
            />
          )}
        </div>
      );
    });

  const containsOnlySimpleValuesAndArrays = (obj: Record<string, any>): boolean => {
    return Object.values(obj).every(
      (value) =>
        isPrimitive(value) ||
        (Array.isArray(value) && !value.some((item) => typeof item === "object" && item !== null))
    );
  };

  const handleTextareaChange = (pathKey: string, newValue: string) => {
    setTextareaValues((prev) => ({ ...prev, [pathKey]: newValue }));
  };

  const renderRecursiveAccordion = (obj: any, path: string[] = []) => {
    if (Array.isArray(obj)) {
      return (
        <div className="mb-4">
          <textarea
            className="w-full h-40 p-2 border rounded"
            value={getTextareaValue(path, obj)}
            onChange={(e) => updateTextareaValue(path, e.target.value)}
          />
        </div>
      );
    }

    // gestion des props "_"
    const entries = Object.entries(obj);
    const groupedEntries: Record<string, Array<[string, any]>> = {};
    const ungroupedEntries: Array<[string, any]> = [];

    for (const [key, value] of entries) {
      const underscoreIndex = key.indexOf('_');
      if (underscoreIndex > 0) {
        const prefix = key.substring(0, underscoreIndex + 1);
        if (!groupedEntries[prefix]) {
          groupedEntries[prefix] = [];
        }
        groupedEntries[prefix].push([key, value]);
      } else {
        ungroupedEntries.push([key, value]);
      }
    }

    // Identifier les groupes qui ont plus d'une entrée pour créer des tabs
    const tabGroups = Object.entries(groupedEntries).filter(([prefix, items]) => items.length > 1);
    const singleItems = Object.entries(groupedEntries).filter(([prefix, items]) => items.length === 1).flatMap(([prefix, items]) => items);

    const allSingleEntries = [...ungroupedEntries, ...singleItems];

    const renderTabGroup = (prefix: string, items: Array<[string, any]>) => {
      return (
        <AccordionItem
          key={`${path.join(".")}.${prefix}group`}
          aria-label={`${prefix.slice(0, -1)} Settings`}
          title={
            <div className="flex justify-between items-center text-small">
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
                <span>
                  {prefix.slice(0, -1)}
                  {" "}
                  {items.some(([itemKey]) => isModified([...path, itemKey], obj[itemKey])) && (
                    <Chip color="primary">Updated</Chip>
                  )}
                </span>
              </div>
            </div>
          }
        >
          <Tabs
            aria-label={`${prefix} tabs`}
            color="primary"
            variant="bordered"
            classNames={{
              tabList: "gap-2 w-full border-b border-divider h-10",
              tab: "max-w-fit px-4 h-8 text-xs",
            }}
          >
            {items.map(([itemKey, itemValue]) => {
              const itemPath = [...path, itemKey];
              const modified = containsOnlySimpleValuesAndArrays(itemValue) ?
                countModifiedFields(itemValue, itemPath) :
                isModified(itemPath, itemValue);

              return (
                <Tab
                  key={itemKey}
                  title={
                    <div className="flex items-center space-x-2">
                      <span>{itemKey.replace(prefix, '')}</span>
                      {((typeof modified === 'number' && modified > 0) || (typeof modified === 'boolean' && modified)) && (
                        <Chip size="sm" variant="faded">
                          {typeof modified === 'number' ? modified : 'Updated'}
                        </Chip>
                      )}
                    </div>
                  }
                >
                  {renderSingleAccordionContent(itemKey, itemValue, itemPath)}
                </Tab>
              );
            })}
          </Tabs>
        </AccordionItem>
      );
    };

    const handleFormatJson = (currentPath: string[], value: any) => {
      const pathKey = currentPath.join(".");
      const formattedJSON = JSON.stringify(
        getEffectiveValue(currentPath, value),
        null,
        2
      );
      setTextareaValues((prev) => ({ ...prev, [pathKey]: formattedJSON }));
    };

    const handleValidateJson = (currentPath: string[]) => {
      const shouldContinue = confirm(
        "Cette action va essayer de parser le JSON. Si le format n'est pas valide, les valeurs ne seront pas mises à jour. Continuer ?"
      );
      if (shouldContinue) {
        try {
          const pathKey = currentPath.join(".");
          const parsed = JSON.parse(textareaValues[pathKey] || "{}");
          updateValueAtPath(currentPath, parsed);
          alert("JSON parsé avec succès !");
        } catch {
          alert("Erreur de format JSON. Veuillez vérifier votre saisie.");
        }
      }
    };

    const renderJsonEditor = (currentPath: string[], value: any) => (
      <div>
        <div>
          <Button
            size="sm"
            color="primary"
            onPress={() => handleFormatJson(currentPath, value)}
          >
            Formater JSON
          </Button>
          <Button
            size="sm"
            color="danger"
            onPress={() => handleValidateJson(currentPath)}
          >
            Valider JSON
          </Button>
        </div>
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <div className="text-small">Edition</div>
            <textarea
              className="w-full h-[50vh] p-2 border rounded text-small"
              value={getTextareaValue(currentPath, value)}
              onChange={(e) => updateTextareaValue(currentPath, e.target.value)}
            />
          </div>
          <div>
            <div className="text-small">Valeur par défaut</div>
            <pre className="w-full h-[50vh] p-2 border rounded text-small bg-black/90 text-white overflow-auto">
              {JSON.stringify(value, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );

    const renderSingleAccordionContent = (key: string, value: any, currentPath: string[]) => {
      const effectiveVal = getEffectiveValue(currentPath, value);

      // Case 1 & 2: array or complex object > textarea
      if (
        Array.isArray(value) ||
        (typeof value === "object" && value !== null && !containsOnlySimpleValuesAndArrays(value))
      ) {
        return renderJsonEditor(currentPath, value);
      }
      // Case 3: object with simple values
      else if (
        typeof value === "object" &&
        value !== null &&
        containsOnlySimpleValuesAndArrays(value)
      ) {
        return (
          <div className="flex flex-col gap-2">{renderFields(value, currentPath)}</div>
        );
      }
      // Case 4: primitive value
      else {
        return (
          <div className="flex items-center gap-2 mb-2">
            <div className="w-40 font-medium">{key}</div>
            {typeof effectiveVal === "boolean" ? (
              <Switch
                isSelected={effectiveVal}
                onValueChange={(value) => {
                  updateValueAtPath(currentPath, value);
                }}
              ></Switch>
            ) : (
              <Input
                value={String(effectiveVal ?? "")}
                onChange={(e) => {
                  if (e.target.value === "true" || e.target.value === "false") {
                    updateValueAtPath(currentPath, e.target.value === "true");
                  } else if (e.target.value !== "" && !Number.isNaN(Number(e.target.value))) {
                    updateValueAtPath(currentPath, Number(e.target.value));
                  } else {
                    updateValueAtPath(currentPath, e.target.value);
                  }
                }}
                className="flex-1"
              />
            )}
          </div>
        );
      }
    };

    return [
      ...tabGroups.map(([prefix, items]) => renderTabGroup(prefix, items)),
      ...allSingleEntries.map(([key, value]) => {
        const currentPath = [...path, key];
        const effectiveVal = getEffectiveValue(currentPath, value);

        // Case 1: array > textarea
      if (Array.isArray(value)) {
        return (
          <AccordionItem
            key={currentPath.join(".")}
            aria-label={key}
            title={
              <div className="flex justify-between items-center text-small">
                <span>
                  {key}
                  {getSettingDescription([...path, key].join('.')) && (
                    <span className="text-xs text-gray-500 ml-1">({getSettingDescription([...path, key].join('.'))})</span>
                  )}
                  {" "}
                  {isModified(currentPath, value) && <Chip color="primary">Updated</Chip>}
                </span>
              </div>
            }
          >
            <div>
              <Button
                size="sm"
                color="primary"
                onPress={() => handleFormatJson(currentPath, value)}
              >
                Formater JSON
              </Button>
              <Button
                size="sm"
                color="danger"
                onPress={() => handleValidateJson(currentPath)}
              >
                Valider JSON
              </Button>
            </div>
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <div className="text-small">Edition</div>
                <textarea
                  className="w-full h-[50vh] p-2 border rounded text-small"
                  value={getTextareaValue(currentPath, value)}
                  onChange={(e) => updateTextareaValue(currentPath, e.target.value)}
                />
              </div>
              <div>
                <div className="text-small">Valeur par défaut</div>
                <pre className="w-full h-[50vh] p-2 border rounded text-small bg-black/90 text-white overflow-auto">
                  {JSON.stringify(value, null, 2)}
                </pre>
              </div>
            </div>
          </AccordionItem>
        );
      }
      // Case 2: object complexe > textarea
      else if (
        typeof value === "object" &&
        value !== null &&
        !containsOnlySimpleValuesAndArrays(value)
      ) {
        const modified = countModifiedFields(value, currentPath);
        return (
          <AccordionItem
            key={currentPath.join(".")}
            aria-label={key}
            title={
              <div className="flex justify-between items-center text-small">
                <span>
                  {key}
                  {getSettingDescription([...path, key].join('.')) && (
                    <span className="text-xs text-gray-500 ml-1">({getSettingDescription([...path, key].join('.'))})</span>
                  )}
                </span>
              </div>
            }
            startContent={
              modified > 0 && (
                <Chip size="md" color="primary">
                  {modified}
                </Chip>
              )
            }
          >
            <div>
              <Button
                size="sm"
                color="primary"
                onPress={() => handleFormatJson(currentPath, value)}
              >
                Formater JSON
              </Button>
              <Button
                size="sm"
                color="danger"
                onPress={() => handleValidateJson(currentPath)}
              >
                Valider JSON
              </Button>
            </div>
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <div className="text-small">Edition</div>
                <textarea
                  className="w-full h-[50vh] p-2 border rounded text-small"
                  value={getTextareaValue(currentPath, value)}
                  onChange={(e) => updateTextareaValue(currentPath, e.target.value)}
                />
              </div>
              <div>
                <div className="text-small">Valeur par défaut</div>
                <pre className="w-full h-[50vh] p-2 border rounded text-small bg-black/90 text-white overflow-auto">
                  {JSON.stringify(value, null, 2)}
                </pre>
              </div>
            </div>
          </AccordionItem>
        );
      }
      // Case 3: objec with simple value
      else if (
        typeof value === "object" &&
        value !== null &&
        containsOnlySimpleValuesAndArrays(value)
      ) {
        const modified = countModifiedFields(value, currentPath);
        return (
          <AccordionItem
            key={currentPath.join(".")}
            aria-label={key}
            title={
              <div className="flex justify-between items-center text-small">
                <span>
                  {key}
                  {getSettingDescription([...path, key].join('.')) && (
                    <span className="text-xs text-gray-500 ml-1">({getSettingDescription([...path, key].join('.'))})</span>
                  )}
                </span>
              </div>
            }
            startContent={
              modified > 0 && (
                <Chip size="md" color="primary">
                  {modified}
                </Chip>
              )
            }
          >
            <div className="flex flex-col gap-2">{renderFields(value, currentPath)}</div>
          </AccordionItem>
        );
      }
      // Case 4: primitive value
      else {
        return (
          <AccordionItem
            key={currentPath.join(".")}
            aria-label={key}
            title={
              <div className="flex justify-between items-center text-small">
                <span>
                  {key}
                  {getSettingDescription([...path, key].join('.')) && (
                    <span className="text-xs text-gray-500 ml-1">({getSettingDescription([...path, key].join('.'))})</span>
                  )}
                  {" "}
                  {isModified(currentPath, value) && <Chip color="primary">Updated</Chip>}
                </span>
              </div>
            }
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-40 font-medium">{key}</div>
              {typeof effectiveVal === "boolean" ? (
                <Switch
                  isSelected={effectiveVal}
                  onValueChange={(value) => {
                    updateValueAtPath(currentPath, value);
                  }}
                ></Switch>
              ) : (
                <Input
                  value={String(effectiveVal ?? "")}
                  onChange={(e) => {
                    if (e.target.value === "true" || e.target.value === "false") {
                      updateValueAtPath(currentPath, e.target.value === "true");
                    } else if (e.target.value !== "" && !Number.isNaN(Number(e.target.value))) {
                      updateValueAtPath(currentPath, Number(e.target.value));
                    } else {
                      updateValueAtPath(currentPath, e.target.value);
                    }
                  }}
                  className="flex-1"
                />
              )}
            </div>
          </AccordionItem>
        );
      }
    }),
    ];
  };

  const renderConfigSection = (key: string, value: any) => {
    const currentPath = [key];

    if (Array.isArray(value)) {
      const isModifiedValue = isModified(currentPath, value);

      return (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold capitalize">
              {key} {isModifiedValue && <Chip color="primary">Updated</Chip>}
            </h2>
          </div>
          <div>
            <Button
              size="sm"
              color="primary"
              onPress={() => {
                const pathKey = currentPath.join(".");
                const formattedJSON = JSON.stringify(
                  getEffectiveValue(currentPath, value),
                  null,
                  2
                );
                setTextareaValues((prev) => ({ ...prev, [pathKey]: formattedJSON }));
              }}
            >
              Formater JSON
            </Button>
            <Button
              size="sm"
              color="danger"
              onPress={() => {
                const shouldContinue = confirm(
                  "Cette action va essayer de parser le JSON. Si le format n'est pas valide, les valeurs ne seront pas mises à jour. Continuer ?"
                );
                if (shouldContinue) {
                  try {
                    const pathKey = currentPath.join(".");
                    const parsed = JSON.parse(textareaValues[pathKey] || "{}");
                    updateValueAtPath(currentPath, parsed);
                    alert("JSON parsé avec succès !");
                  } catch {
                    alert("Erreur de format JSON. Veuillez vérifier votre saisie.");
                  }
                }
              }}
            >
              Valider JSON
            </Button>
          </div>
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-small">Edition</div>
              <textarea
                className="w-full h-[50vh] p-2 border rounded text-small"
                value={getTextareaValue(currentPath, value)}
                onChange={(e) => updateTextareaValue(currentPath, e.target.value)}
              />
            </div>
            <div>
              <div className="text-small">Valeur par défaut</div>
              <pre className="w-full h-[50vh] p-2 border rounded text-small bg-black/90 text-white overflow-auto">
                {JSON.stringify(value, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      );
    }

    if (
      typeof value === "object" &&
      value !== null &&
      !Object.values(value).every((v) => isPrimitive(v))
    ) {
      return (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold capitalize">{key}</h2>
          </div>

          {textareaValues[currentPath.join(".")] !== undefined ? (
            <textarea
              className="w-full h-64 p-2 border rounded"
              value={textareaValues[currentPath.join(".")]}
              onChange={(e) => handleTextareaChange(currentPath.join("."), e.target.value)}
            />
          ) : (
            <Accordion className="flex flex-col gap-2">
              {renderRecursiveAccordion(value, currentPath)}
            </Accordion>
          )}
        </div>
      );
    }

    if (
      typeof value === "object" &&
      value !== null &&
      Object.values(value).every((v) => isPrimitive(v))
    ) {
      return <div className="mt-4 flex flex-col gap-2">{renderFields(value, currentPath)}</div>;
    }
  };

  const saveConfig = async () => {
    try {
      const isAllValid = Object.entries(textareaValues).every(([pathKey, value]) => {
        try {
          JSON.parse(value);
          return true;
        } catch {
          console.error(`JSON invalide pour ${pathKey}`);
          return false;
        }
      });

      if (!isAllValid) {
        const shouldContinue = confirm(
          "Certains champs JSON contiennent du contenu non valide. Voulez-vous quand même tenter de sauvegarder les données valides ?"
        );
        if (!shouldContinue) return;
      }
      await axiosPrivate.post(`/useCases/custom`, overrides);
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la sauvegarde.");
    }
  };
  if (!baseConfig || !clientConfig || selectedTab === null) {
    return <div>Chargement des configurations...</div>;
  }
  return (
    <div className="p-4">
      <h1 className="text-lg font-bold mb-4">Éditeur de Configuration</h1>
      <Tabs
        aria-label="Configuration Tabs"
        selectedKey={selectedTab}
        onSelectionChange={(key) => setSelectedTab(key as string)}
        color="primary"
        variant="underlined"
        classNames={{
          tabList: "gap-4 w-full border-b border-divider",
          cursor: "w-full bg-[#22d3ee]",
          tab: "max-w-fit px-0 h-12 text-xs",
          tabContent: "group-data-[selected=true]:text-[#06b6d4]",
        }}
      >
        {Object.entries(baseConfig).map(([key, value]) => (
          <Tab
            key={key}
            title={
              <div className="flex items-center space-x-2">
                <span className="capitalize">{key}</span>
                {countModifiedFields(value, [key]) > 0 && (
                  <Chip size="sm" variant="faded">
                    {countModifiedFields(value, [key])}
                  </Chip>
                )}
              </div>
            }
          >
            {renderConfigSection(key, value)}
          </Tab>
        ))}
      </Tabs>
      <Button className="mt-6" onPress={saveConfig}>
        Sauvegarder
      </Button>
    </div>
  );
}
